import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  styled,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { Icon } from "../Icon";
import { COLOR_CONSTANTS } from "../../constants/styles/color";
import { TYPOGRAPHY_SCALE } from "../../constants/styles";
import { SPACING_CONSTANTS } from "../../constants/styles/spacing";

/**
 * 데이터 섹션 헤더 — 좌측 번호 배지 + 제목 + 펼침/접힘 화살표.
 * `children` 을 두면 펼침 영역으로 표시되어 `DataCell`/`DataList` 등을 끼울 수 있다.
 */
interface DataTitleProps {
  /**
   * 좌측 번호 배지 (선택)
   */
  number?: string | number;
  /**
   * 제목 텍스트
   */
  title: string;
  /**
   * 오른쪽 아이콘 표시 여부
   */
  showRightIcon?: boolean;
  /**
   * 열림 상태 (화살표 방향 결정)
   */
  open?: boolean;
  /**
   * 클릭 핸들러
   */
  onClick?: () => void;
  /**
   * 타이틀 옆 아이콘 이름
   */
  titleIcon?: string;
  /**
   * 타이틀 아이콘 클릭 핸들러
   */
  onTitleIconClick?: () => void;
  /**
   * 열렸을 때 표시할 컨텐츠
   */
  children?: React.ReactNode;
  /**
   * 추가 CSS 클래스명
   */
  className?: string;
}

const StyledAccordion = styled(Accordion)({
  width: "100%",
  boxShadow: "none",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: 0,
  },
});

const StyledAccordionSummary = styled(AccordionSummary)({
  minHeight: "36px",
  maxHeight: "36px",
  height: "36px",
  padding: "4px 8px",
  "&.Mui-expanded": {
    minHeight: "36px",
    maxHeight: "36px",
    height: "36px",
    backgroundColor: COLOR_CONSTANTS.COLOR.GREY[100],
  },
  "& .MuiAccordionSummary-content": {
    margin: 0,
    alignItems: "center",
  },
});

const StyledAccordionDetails = styled(AccordionDetails)({
  padding: `${SPACING_CONSTANTS.spacing[4]}px ${SPACING_CONSTANTS.spacing[2]}px ${SPACING_CONSTANTS.spacing[2]}px ${SPACING_CONSTANTS.spacing[2]}px`,
  backgroundColor: COLOR_CONSTANTS.COLOR.GREY[100],
});

const StyledContentArea = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: `${SPACING_CONSTANTS.spacing[2]}px`,
  width: "100%",
});

const StyledNumberContainer = styled(Box)({
  width: "20px",
  height: "20px",
  borderRadius: "50%",
  backgroundColor: COLOR_CONSTANTS.COLOR.BLUE_GREY[300],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const StyledNumberText = styled(Typography)({
  ...TYPOGRAPHY_SCALE.caption1,
  color: COLOR_CONSTANTS.common.white_states.main,
  textAlign: "center",
});

const StyledTitleContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: `${SPACING_CONSTANTS.spacing[3]}px`,
  flex: 1,
});

const StyledTitleText = styled(Typography)({
  ...TYPOGRAPHY_SCALE.subtitle1,
  color: COLOR_CONSTANTS.text.primary,
  fontSize: "15px",
});

const StyledTitleIconContainer = styled(Box)({
  width: "16px",
  height: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderRadius: SPACING_CONSTANTS.borderRadius.xs,
  "&:hover": {
    backgroundColor: COLOR_CONSTANTS.action.hover,
  },
  "&:active": {
    backgroundColor: COLOR_CONSTANTS.action.selected,
  },
});

const StyledExpandIcon = styled(Box)({
  width: "16px",
  height: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

/**
 * DataTitle 컴포넌트
 *
 * MUI Accordion을 사용한 데이터 항목의 제목을 표시하는 컴포넌트입니다.
 * 숫자, 제목 텍스트, 그리고 선택적으로 오른쪽 아이콘을 포함할 수 있습니다.
 */
export const DataTitle: React.FC<DataTitleProps> = ({
  number,
  title,
  showRightIcon = false,
  open,
  onClick,
  titleIcon,
  onTitleIconClick,
  children,
  className,
}) => {
  const handleTitleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTitleIconClick?.();
  };

  return (
    <StyledAccordion className={className} expanded={open} onChange={onClick}>
      <StyledAccordionSummary
        expandIcon={
          showRightIcon && (
            <StyledExpandIcon>
              <Icon name="arrow/top" size={16} />
            </StyledExpandIcon>
          )
        }
      >
        <StyledContentArea>
          {number !== undefined && (
            <StyledNumberContainer>
              <StyledNumberText>{number}</StyledNumberText>
            </StyledNumberContainer>
          )}

          <StyledTitleContainer>
            <StyledTitleText>{title}</StyledTitleText>
            {titleIcon && (
              <StyledTitleIconContainer onClick={handleTitleIconClick}>
                <Icon name="etc/edit" size={16} />
              </StyledTitleIconContainer>
            )}
          </StyledTitleContainer>
        </StyledContentArea>
      </StyledAccordionSummary>

      {children && <StyledAccordionDetails>{children}</StyledAccordionDetails>}
    </StyledAccordion>
  );
};

export type { DataTitleProps };
